/**
 * Safe wrapper for localStorage operations.
 * Handles try-catch blocks and JSON parsing/stringification.
 */

export const storage = {
  /**
   * Retrieve and parse a JSON value from localStorage.
   * Returns defaultValue if key doesn't exist or parsing fails.
   */
  getJson: <T>(key: string, defaultValue: T | null = null): T | null => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading/parsing ${key} from localStorage:`, error);
      return defaultValue;
    }
  },

  /**
   * Stringify and save a value to localStorage.
   */
  setJson: (key: string, value: any): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Error writing ${key} to localStorage:`, error);
    }
  },

  /**
   * Retrieve a raw string from localStorage.
   */
  getString: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  },

  /**
   * Save a raw string to localStorage.
   */
  setString: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Error writing ${key} to localStorage:`, error);
    }
  },

  /**
   * Remove an item from localStorage.
   */
  removeItem: (key: string): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Error removing ${key} from localStorage:`, error);
    }
  }
};
